import { useState, useCallback } from "react";
import type { FileItem, FileListCallbacks } from "./types";

/**
 * Hook for managing file list state and actions
 */
export const useFileListState = (callbacks: FileListCallbacks) => {
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set(),
  );
  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFileIndex, setViewerFileIndex] = useState(0);
  const [deleteDialogFile, setDeleteDialogFile] = useState<FileItem | null>(
    null,
  );

  const handleDownload = useCallback(
    async (file: FileItem) => {
      if (!callbacks.onDownload) return;

      setDownloadingFiles((prev) => new Set(prev).add(file.id));
      try {
        await callbacks.onDownload(file);
      } finally {
        setDownloadingFiles((prev) => {
          const next = new Set(prev);
          next.delete(file.id);
          return next;
        });
      }
    },
    [callbacks.onDownload],
  );

  const handlePreview = useCallback(
    (file: FileItem, index: number) => {
      // Check if file viewer is enabled by checking if getFileUrl exists
      const hasViewer = !!callbacks.getFileUrl;
      if (hasViewer) {
        setViewerFileIndex(index);
        setViewerOpen(true);
      } else if (callbacks.onPreview) {
        callbacks.onPreview(file, index);
      }
    },
    [callbacks.onPreview, callbacks.getFileUrl],
  );

  const handleItemClick = useCallback(
    (
      e: React.MouseEvent,
      file: FileItem,
      index: number,
      showPreview: boolean,
      isDeleting: boolean,
    ) => {
      // Don't trigger preview if clicking on action buttons
      if ((e.target as Element).closest("[data-action-button]")) {
        return;
      }
      if (showPreview && !isDeleting) {
        handlePreview(file, index);
      }
    },
    [handlePreview],
  );

  const handleViewerClose = useCallback(() => {
    setViewerOpen(false);
  }, []);

  const handleDeleteClick = useCallback((file: FileItem) => {
    setDeleteDialogFile(file);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialogFile || !callbacks.onDelete) return;

    setDeletingFiles((prev) => new Set(prev).add(deleteDialogFile.id));
    setError(null);

    try {
      await callbacks.onDelete(deleteDialogFile);
    } catch (error) {
      console.error("Failed to delete file:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to delete file. Please try again.",
      );
    } finally {
      setDeletingFiles((prev) => {
        const next = new Set(prev);
        next.delete(deleteDialogFile.id);
        return next;
      });
      setDeleteDialogFile(null);
    }
  }, [deleteDialogFile, callbacks.onDelete]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogFile(null);
  }, []);

  return {
    // State
    downloadingFiles,
    deletingFiles,
    error,
    setError,
    viewerOpen,
    viewerFileIndex,
    deleteDialogFile,

    // Handlers
    handleDownload,
    handlePreview,
    handleItemClick,
    handleViewerClose,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
  };
};

/**
 * Hook for managing file display logic
 */
export const useFileDisplay = (files: FileItem[], maxItems?: number) => {
  const displayFiles = maxItems ? files.slice(0, maxItems) : files;
  const hasMore = maxItems ? files.length > maxItems : false;
  const remainingCount = hasMore ? files.length - maxItems! : 0;

  return {
    displayFiles,
    hasMore,
    remainingCount,
  };
};
